import { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router";
import { API_BASE_URL, API_URLS, LOCALSTORAGE_TOKEN_ITEM } from "constants/api";

export interface SocialLink {
  id: string;
  platform: string;
  url: string;
  status: string;
  social_platform_id: string;
}

export const useSocialLinks = (artistIdFromProps?: string) => {
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);
  const [fetching, setFetching] = useState<boolean>(false);
  const [updating, setUpdating] = useState<boolean>(false);
  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM);
  const params = useParams<{ artist: string }>();
  const {id}  =  useParams()
  const artistId = String( id)

  useEffect(() => {
    if (artistId) {
      getSocialLinks();
    }
  }, [artistId]);
  
  const getSocialLinks = async () => {
    try {
      setFetching(true);
      const response = await axios.get(
        `${API_BASE_URL}${API_URLS.Social.replace("{artist}", artistId)}`,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );      
      setSocialLinks(response.data.data);
    } catch (e) {
      console.error("Failed to fetch social links:", e);
    } finally {
      setFetching(false);
    }
  };

  const createSocialLink = async (link: string) => {
    try {
      setFetching(true);
      setUpdating(true);  
      const response = await axios.post<{ data: SocialLink }>(
        `${API_BASE_URL}${API_URLS.CreateSocialLink.replace("{artist}", artistId)}`,
        { link },
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const newLink = response.data.data;
      setSocialLinks((prev) => [...prev, newLink]);
    } catch (error) {
      console.error("Failed to create social link:", error);
    } finally {
      setUpdating(false);
      setFetching(false);

    }
  };
  

  const updateSocialLink = async (linkId: string, link: string) => {
    try {
      setUpdating(true);
      setFetching(true);

      const response = await axios.put<{ data: SocialLink }>(
        `${API_BASE_URL}${API_URLS.UpdateSocialLink
          .replace("{artist}", artistId)
          .replace("{social}", linkId)}`,
        { link },
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
  
      const updated = response.data.data;
      setSocialLinks((prev) =>
        prev.map((item) => (item.id === linkId ? updated : item))
      );
    } catch (error) {
      console.error("Failed to update social link:", error);
    } finally {
      setFetching(false);

      setUpdating(false);
    }
  };
  
  const deleteSocialLink = async (linkId: string) => {
    try {
      setFetching(true);
  
      const response = await axios.delete(
        `${API_BASE_URL}${API_URLS.UpdateSocialLink
          .replace("{artist}", artistId)
          .replace("{social}", linkId)}`,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      getSocialLinks()
    } catch (e) {
      console.error("Failed to delete social link:", e);
    } finally {
      setFetching(false);
    }
  };
  

  return {
    socialLinks,
    fetching,
    updating,
    getSocialLinks,
    createSocialLink,
    updateSocialLink,
    deleteSocialLink,
    setSocialLinks,
  };
};
